import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { OverlayHelper } from '../../../../helper/overlay.helper';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import { AvailableForConnect, ContactType } from '../../../../models/contacts/contacts.model';
import { IContact, LeadDataObject } from '../../../../models/leads/leads.model';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { PhoneNumberHelper } from '../../../../helper/phone-number.helper';
import { ContactFormComponent } from '../../../shared-module/components/contact-form/contact-form.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseCustomDialog } from '../../../shared-module/dialogs/base-custom-dialog';
import { Store } from '@ngxs/store';
import { HideLoader, ShowLoader } from '../../../store/loader/loader.actions';
import { ValidatorHelper } from '../../../../helper/validator.helper';
import { ContactHelper } from '../../../../helper/contact.helper';

@Component({
  selector: 'fi-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  standalone: false,
})
export class LinkComponent extends BaseCustomDialog implements OnDestroy {

  leadDetails: LeadDataObject;
  label = "Connect contact";
  contactDetails: IContact;
  @Output() popupClosed = new EventEmitter<boolean>();

  contactForm = this.fb.group({
    Id: this.fb.control(null),
    ContactTypeId: this.fb.control(2),
    ContactMethodId: this.fb.control(1),
    Salutation: this.fb.control('', Validators.required),
    FirstName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    LastName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    JobTitle: this.fb.control('', [Validators.pattern(ValidatorHelper.positionValidator)]),
    Email: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    AdditionalEmail: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    Phone: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    PhonePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    Mobile: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    PhoneOther: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    MobilePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    PhoneOtherPrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    LanguageCode: this.fb.control('', Validators.required),
    Company: this.fb.control('', Validators.pattern(ValidatorHelper.companyValidator)),
    Address: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    AddressExt: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    ZipCode: this.fb.control('', Validators.pattern(ValidatorHelper.zipValidator)),
    City: this.fb.control('', Validators.pattern(ValidatorHelper.cityValidator)),
    ProvinceCode: this.fb.control(''),
    CountryCode: this.fb.control(''),
    VatNumber: this.fb.control('', Validators.pattern(ValidatorHelper.vatNumberValidator)),
    Website: this.fb.control('', Validators.pattern(ValidatorHelper.urlValidator)),
    Title: this.fb.control('')
  });

  requestForm = this.fb.group({
    LeadTypeId: this.fb.control(null),
    LeadSourceId: this.fb.control(null),
    LeadText: this.fb.control('')
  });

  filteredData: AvailableForConnect[] = [];
  showContact = false;
  private lastSearch = '';

  getItemText = ( item: AvailableForConnect ): string => {
    const name = [item.FirstName, item.LastName].filter(( part ) => !!part?.trim()).join(' ');
    const location = [item.City, item.Address].filter(( part ) => !!part?.trim()).join(', ');

    return [name, item.Company, location, item.Email]
      .filter(( part ) => !!part?.trim())
      .join(' · ');
  }

  contactId: number;

  @ViewChild(ContactFormComponent) contactFormComponent: ContactFormComponent;
  noContactIsSelected = false;

  constructor(
    public dialogRef: MatDialogRef<LinkComponent>,
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      leadDetails: LeadDataObject,
      contactDetails: IContact,
      availableForConnect: AvailableForConnect[],
      label: string
    },
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    super(dialogRef);
    if ( dialogData ) {
      this.leadDetails = this.dialogData.leadDetails;
      this.contactDetails = this.dialogData.contactDetails;
      this.label = this.dialogData.label;
    }
  }

  ngOnDestroy(): void {
    OverlayHelper.unfixBody();
  }

  closeDialog( closedOnSuccess: boolean ): void {
    this.clearClassAndCloseDialog(this.dialogRef, closedOnSuccess ? 'success' : '');
  }

  onClosePopup(): void {
    this.closeDialog(false);
  }

  onOverlayClick( event: Event ): void {

    if ( event.target === event.currentTarget ) {
      this.onClosePopup();
    }
  }

  connectContact(): void {
    if ( !this.contactId || !this.leadDetails ) {
      this.noContactIsSelected = true;
      return;
    } else {
      this.noContactIsSelected = false;
    }
    this.store.dispatch(new ShowLoader());

    this.contactsDataService.connectContact(this.contactId, this.leadDetails.Id).subscribe(( { success, error } ) => {
      this.store.dispatch(new HideLoader());

      if ( success ) {
        this.closeDialog(true);
      } else {
        console.log(error);
      }
    });
  }

  toggleContact(): void {
    this.showContact = !this.showContact;
    if ( !this.showContact ) {
      this.cleanForm();
      this.dialogRef.removePanelClass('contact-form');
    } else {
      this.dialogRef.updateSize('100%', '100%');
      this.dialogRef.addPanelClass('contact-form');
    }
  }

  cleanForm(): void {
    this.contactForm.reset();
    this.contactForm.controls.ContactTypeId.setValue(2);
    this.contactForm.controls.ContactMethodId.setValue(1);
  }

  onSubmit(): void {
    if ( this.contactForm.valid && !this.contactFormComponent.isInvalidProvinceCode(true) && !this.contactFormComponent.checkIfEmailOrPhoneNeedsToBeFilled() ) {
      this.store.dispatch(new ShowLoader());

      const isPrivate = +this.contactForm.controls.ContactTypeId.value === ContactHelper.CONTACT_TYPE_PRIVATE;

      this.contactsDataService.connectNewContact(
        this.leadDetails.Id,
        {
          ...this.contactForm.getRawValue() as any,
          SalutationId: +this.contactForm.controls.Salutation.value,
          Title: isPrivate ? undefined : this.contactForm.controls.Title.value,
          Company: isPrivate ? undefined : this.contactForm.controls.Company.value,
          JobTitle: isPrivate ? undefined : this.contactForm.controls.JobTitle.value,
          VatNumber: isPrivate ? undefined : this.contactForm.controls.VatNumber.value,
          Website: isPrivate ? undefined : this.contactForm.controls.Website.value,
          PhonePrefix: this.contactForm.controls.Phone.value?.trim() ? this.contactForm.controls.PhonePrefix.value : null,
          MobilePrefix: this.contactForm.controls.Mobile.value?.trim() ? this.contactForm.controls.MobilePrefix.value : null,
          ConsentMarketing: this.contactFormComponent.consentMarketing,
          ConsentThirdParties: this.contactFormComponent.consentThirdParties,
        }
      ).subscribe(( { success, error } ) => {
        this.store.dispatch(new HideLoader());

        if ( success ) {
          this.closeDialog(true);
        } else {
          this.contactFormComponent.showErrors = true;
          this.contactFormComponent.contactExistMessage = true;

          document.querySelector('.mat-mdc-dialog-container')?.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      });
    } else {
      this.contactFormComponent.showErrors = true;
      this.contactFormComponent.scrollToInvalidContactField();
    }
  }

  onFilter( text = '' ): void {
    this.lastSearch = text;

    if ( !text?.trim().length ) {
      return;
    }

    this.contactsDataService.getAvailableForConnect(this.leadDetails.Id, text).subscribe(( { data, success } ) => {
      if ( success && text === this.lastSearch ) {
        this.filteredData = data;
      }
    })
  }

  protected readonly ContactHelper = ContactHelper;
}
